import React, { useState, useEffect } from 'react';
import { 
  Users, Search, ShieldAlert, ShieldCheck, AlertCircle, 
  Coins, FileText, CheckCircle2, XCircle, Lock, RefreshCw,
  ExternalLink, Sparkles, Filter, ChevronLeft, ChevronRight,
  Upload, CheckCircle, Clock
} from 'lucide-react';
import { managerAPI } from '../utils/api';

const ManagerUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [quota, setQuota] = useState({ month: '', bans_used: 0, remaining_bans: 30, monthly_limit: 30 });
  const [country, setCountry] = useState('India');

  // Modals state
  const [selectedUser, setSelectedUser] = useState(null);
  const [showBanModal, setShowBanModal] = useState(false);
  const [banReason, setBanReason] = useState('');
  const [banProofUrl, setBanProofUrl] = useState('');
  const [banHours, setBanHours] = useState(24);
  const [proofFile, setProofFile] = useState(null);
  const [proofUploading, setProofUploading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // Unban with coins modal state
  const [showCoinsModal, setShowCoinsModal] = useState(false);
  const [unbanFee, setUnbanFee] = useState(5000);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const res = await managerAPI.getUsers({ page, limit: 15, search });
      if (res.data?.success) {
        setUsers(res.data.users || []);
        setTotal(res.data.total || 0);
        if (res.data.quota) setQuota(res.data.quota);
        if (res.data.country) setCountry(res.data.country);
      }
    } catch (err) {
      console.error("Failed to load users:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [page]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setPage(1);
    fetchUsers();
  };

  const handleProofFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setProofFile(file);
    try {
      setProofUploading(true);
      const fd = new FormData();
      fd.append('file', file);
      const res = await managerAPI.uploadProof(fd);
      if (res.data?.success && res.data?.url) {
        setBanProofUrl(res.data.url);
      }
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to upload proof file.');
    } finally {
      setProofUploading(false);
    }
  };

  const handleBan = async (e) => {
    e.preventDefault();
    if (!banReason.trim()) {
      alert("Ban reason is mandatory.");
      return;
    }
    if (!banProofUrl.trim()) {
      alert("Proof file upload is mandatory. Manager must upload screenshot or evidence file to ban any user.");
      return;
    }

    try {
      setActionLoading(true);
      const res = await managerAPI.banUser(selectedUser.id, {
        reason: banReason,
        proof_url: banProofUrl,
        ban_hours: banHours
      });
      alert(res.data?.message || "User banned successfully.");
      setShowBanModal(false);
      setBanReason('');
      setBanProofUrl('');
      setProofFile(null);
      setBanHours(24);
      setSelectedUser(null);
      fetchUsers();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to ban user.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleUnban = async (user) => {
    if (!window.confirm(`Are you sure you want to unban user "${user.first_name || user.xoxo_id}"?`)) {
      return;
    }

    try {
      setActionLoading(true);
      const res = await managerAPI.unbanUser(user.id, { note: "Direct unban by Manager" });
      alert(res.data?.message || "User unbanned successfully.");
      fetchUsers();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to unban user.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleUnbanWithCoins = async (e) => {
    e.preventDefault();
    try {
      setActionLoading(true);
      const res = await managerAPI.unbanUserWithCoins(selectedUser.id, { coins: unbanFee });
      alert(res.data?.message || "User unbanned via coin payment.");
      setShowCoinsModal(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to unban user with coins.");
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="space-y-8 animate-in fade-in duration-500">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 pb-6 border-b border-white/5">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-extrabold text-white tracking-tight">Country Users Management</h1>
            <span className="px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 font-bold text-xs uppercase">
              {country} Locked
            </span>
          </div>
          <p className="text-slate-400 text-sm mt-1">
            Monitor and manage active users in your assigned country. Enforce community guidelines with audited actions.
          </p>
        </div>

        {/* Quota Tracker Badge */}
        <div className="flex items-center gap-4 bg-gradient-to-r from-indigo-900/30 to-purple-900/30 border border-indigo-500/20 rounded-2xl p-4">
          <div className="w-12 h-12 rounded-xl bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-center text-indigo-400">
            <ShieldAlert size={24} />
          </div>
          <div>
            <div className="text-xs text-slate-400 font-medium">Monthly Ban Quota ({quota.month || 'Current'})</div>
            <div className="flex items-center gap-2 mt-0.5">
              <span className="text-lg font-black text-white">{quota.bans_used} / {quota.monthly_limit} IDs</span>
              <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${
                quota.remaining_bans > 5 ? 'bg-emerald-500/20 text-emerald-300' : 'bg-rose-500/20 text-rose-300'
              }`}>
                {quota.remaining_bans} Left
              </span>
            </div>
            <p className="text-[10px] text-slate-400 mt-0.5">Strict security limit: max 30 bans/month with proof</p>
          </div>
        </div>
      </div>

      {/* Search & Filter Bar */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between items-center bg-white/[0.02] border border-white/5 p-4 rounded-2xl">
        <form onSubmit={handleSearchSubmit} className="flex-1 w-full flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
            <input
              type="text"
              placeholder="Search by User ID, XOXO ID, Name, Phone..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full bg-[#030712] border border-white/10 rounded-xl pl-10 pr-4 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-indigo-500 transition-colors"
            />
          </div>
          <button
            type="submit"
            className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-semibold rounded-xl transition-colors cursor-pointer"
          >
            Search
          </button>
        </form>

        <button 
          onClick={fetchUsers}
          className="p-2.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-slate-300 transition-colors cursor-pointer"
          title="Refresh"
        >
          <RefreshCw size={18} />
        </button>
      </div>

      {/* Users Table */}
      <div className="bg-[#0b0f19] border border-white/5 rounded-2xl overflow-hidden shadow-xl">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm text-slate-300">
            <thead className="bg-white/[0.02] text-xs uppercase text-slate-400 tracking-wider border-b border-white/5">
              <tr>
                <th className="py-4 px-6">User Profile</th>
                <th className="py-4 px-6">XOXO ID</th>
                <th className="py-4 px-6">Type / Role</th>
                <th className="py-4 px-6">VIP Tier</th>
                <th className="py-4 px-6">Coins Balance</th>
                <th className="py-4 px-6">Status</th>
                <th className="py-4 px-6 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {loading ? (
                <tr>
                  <td colSpan="7" className="py-12 text-center text-slate-400">
                    <div className="flex items-center justify-center gap-2">
                      <RefreshCw size={18} className="animate-spin text-indigo-400" />
                      <span>Loading country users...</span>
                    </div>
                  </td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan="7" className="py-12 text-center text-slate-400">
                    No users found matching your search in {country}.
                  </td>
                </tr>
              ) : (
                users.map((u) => (
                  <tr key={u.id} className="hover:bg-white/[0.01] transition-colors">
                    <td className="py-4 px-6">
                      <div className="flex items-center gap-3">
                        <img 
                          src={u.avatar_url || `https://api.dicebear.com/7.x/bottts/svg?seed=${u.xoxo_id || u.id}`}
                          alt={u.first_name}
                          className="w-10 h-10 rounded-full object-cover border border-white/10"
                        />
                        <div>
                          <div className="font-bold text-white">
                            {u.first_name || ''} {u.last_name || ''}
                          </div>
                          <div className="text-xs text-slate-500">{u.email || u.phone || 'No direct contact'}</div>
                        </div>
                      </div>
                    </td>
                    <td className="py-4 px-6 font-mono text-indigo-400 font-bold">
                      {u.xoxo_id || u.user_id || u.id}
                    </td>
                    <td className="py-4 px-6">
                      <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-white/5 border border-white/10 text-slate-300">
                        {u.user_type || 'USER'}
                      </span>
                    </td>
                    <td className="py-4 px-6">
                      {u.vip_tier ? (
                        <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-amber-500/10 border border-amber-500/20 text-amber-300 flex items-center gap-1 w-max">
                          <Sparkles size={12} /> {u.vip_tier}
                        </span>
                      ) : (
                        <span className="text-xs text-slate-500">Regular</span>
                      )}
                    </td>
                    <td className="py-4 px-6 font-mono font-bold text-amber-400">
                      {Number(u.coins_balance || 0).toLocaleString()} 🪙
                    </td>
                    <td className="py-4 px-6">
                      {u.is_active !== false ? (
                        <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 flex items-center gap-1 w-max">
                          <CheckCircle2 size={12} /> Active
                        </span>
                      ) : (
                        <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-rose-500/10 border border-rose-500/20 text-rose-400 flex items-center gap-1 w-max">
                          <XCircle size={12} /> Banned
                        </span>
                      )}
                    </td>
                    <td className="py-4 px-6 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {u.is_active !== false ? (
                          ['AGENCY', 'MANAGER', 'BD', 'SUPER_ADMIN', 'ADMIN'].includes((u.user_type || '').toUpperCase()) ? (
                            <span 
                              className="px-2.5 py-1 rounded-xl bg-white/5 border border-white/10 text-slate-500 text-xs font-semibold cursor-not-allowed"
                              title="Executive accounts (Agency, Manager, BD, Admin) cannot be banned by Manager"
                            >
                              Protected ID
                            </span>
                          ) : (
                            <button
                              onClick={() => {
                                setSelectedUser(u);
                                setBanHours(24);
                                setBanProofUrl('');
                                setProofFile(null);
                                setShowBanModal(true);
                              }}
                              className="px-3 py-1.5 rounded-xl bg-rose-500/10 hover:bg-rose-500/20 border border-rose-500/20 text-rose-300 text-xs font-bold transition-all cursor-pointer"
                            >
                              Ban ID
                            </button>
                          )
                        ) : (
                          <>
                            <button
                              onClick={() => handleUnban(u)}
                              disabled={actionLoading}
                              className="px-3 py-1.5 rounded-xl bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/20 text-emerald-300 text-xs font-bold transition-all cursor-pointer"
                            >
                              Direct Unban
                            </button>
                            <button
                              onClick={() => {
                                setSelectedUser(u);
                                setShowCoinsModal(true);
                              }}
                              className="px-3 py-1.5 rounded-xl bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/20 text-amber-300 text-xs font-bold transition-all cursor-pointer flex items-center gap-1"
                            >
                              <Coins size={12} /> Coins Unban
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="p-4 border-t border-white/5 flex items-center justify-between text-xs text-slate-400">
          <div>Showing {users.length} of {total} users in {country}</div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="p-2 rounded-lg bg-white/5 hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed text-white transition-colors"
            >
              <ChevronLeft size={16} />
            </button>
            <span className="px-3 py-1 font-semibold text-white">Page {page}</span>
            <button
              onClick={() => setPage(p => p + 1)}
              disabled={users.length < 15}
              className="p-2 rounded-lg bg-white/5 hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed text-white transition-colors"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* BAN USER MODAL */}
      {showBanModal && selectedUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-in fade-in">
          <div className="bg-[#0f1026] border border-rose-500/30 rounded-3xl p-8 max-w-lg w-full shadow-2xl relative">
            <div className="flex items-center gap-3 text-rose-400 mb-4">
              <ShieldAlert size={28} />
              <h3 className="text-xl font-black text-white">Ban User Account</h3>
            </div>

            <div className="p-4 rounded-xl bg-rose-500/10 border border-rose-500/20 mb-6 text-xs text-rose-200 space-y-1">
              <div className="font-bold flex items-center gap-1.5">
                <AlertCircle size={14} /> Strict Manager Policy:
              </div>
              <div>• Monthly Limit: <strong>30 bans maximum</strong> ({quota.remaining_bans} remaining this month).</div>
              <div>• Proof is <strong>STRICTLY MANDATORY</strong>. Every ban is saved to Audit Log with timestamp.</div>
            </div>

            <div className="mb-4 p-3 rounded-xl bg-white/[0.03] border border-white/5 flex items-center gap-3">
              <div className="font-bold text-white text-sm">
                Target: {selectedUser.first_name} {selectedUser.last_name}
              </div>
              <div className="font-mono text-xs text-indigo-400">({selectedUser.xoxo_id || selectedUser.id})</div>
            </div>

            <form onSubmit={handleBan} className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-2 flex items-center justify-between">
                  <span>Ban Timing (1 Hr to 24 Hr Max) *</span>
                  <span className="text-rose-400 font-mono font-bold flex items-center gap-1">
                    <Clock size={12} /> {banHours} Hour{banHours > 1 ? 's' : ''}
                  </span>
                </label>
                <select
                  value={banHours}
                  onChange={(e) => setBanHours(Number(e.target.value))}
                  className="w-full bg-[#030712] border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none focus:border-rose-500 transition-colors cursor-pointer"
                >
                  {[1, 2, 3, 4, 6, 8, 12, 16, 20, 24].map((hr) => (
                    <option key={hr} value={hr}>
                      {hr} Hour{hr > 1 ? 's' : ''} {hr === 24 ? '(Max 24 Hours Allowed)' : ''}
                    </option>
                  ))}
                </select>
                <p className="text-[11px] text-slate-400 mt-1">Managers can only ban IDs for a minimum of 1 hour up to maximum 24 hours.</p>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-2">
                  Ban Reason *
                </label>
                <textarea
                  rows="3"
                  required
                  placeholder="Explain why this ID is being banned (e.g. Abusive behavior in room, spamming, fraud)..."
                  value={banReason}
                  onChange={(e) => setBanReason(e.target.value)}
                  className="w-full bg-[#030712] border border-white/10 rounded-xl p-3 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-rose-500 transition-colors"
                ></textarea>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-2">
                  Violation Proof (File Upload Mandatory) *
                </label>
                <label className="flex items-center justify-center gap-2 p-3 bg-white/5 hover:bg-white/10 border border-dashed border-white/20 hover:border-rose-500/50 rounded-xl text-xs text-slate-300 cursor-pointer transition-colors">
                  <Upload size={16} className="text-rose-400" />
                  <span>
                    {proofUploading 
                      ? 'Uploading proof file...' 
                      : (proofFile ? proofFile.name : 'Choose Screenshot / Proof Document')}
                  </span>
                  <input
                    type="file"
                    accept="image/*,application/pdf"
                    required={!banProofUrl}
                    onChange={handleProofFileChange}
                    className="hidden"
                  />
                </label>
                {banProofUrl && (
                  <div className="mt-2 flex items-center justify-between p-2 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-xs text-emerald-300">
                    <span className="flex items-center gap-1.5 truncate">
                      <CheckCircle size={14} className="shrink-0" />
                      Proof uploaded!
                    </span>
                    <a href={banProofUrl} target="_blank" rel="noreferrer" className="text-indigo-400 hover:text-indigo-300 underline font-semibold shrink-0">
                      View File
                    </a>
                  </div>
                )}
                <p className="text-[11px] text-slate-400 mt-1">Upload direct screenshot or recording evidence from device.</p>
              </div>

              <div className="flex items-center gap-3 pt-4 border-t border-white/10">
                <button
                  type="button"
                  onClick={() => setShowBanModal(false)}
                  className="flex-1 py-3 rounded-xl bg-white/5 hover:bg-white/10 text-slate-300 text-sm font-semibold transition-colors cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={actionLoading || quota.remaining_bans <= 0}
                  className="flex-1 py-3 rounded-xl bg-rose-600 hover:bg-rose-500 disabled:opacity-50 text-white text-sm font-bold transition-colors cursor-pointer"
                >
                  {actionLoading ? 'Banning...' : 'Confirm Ban ID'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* UNBAN WITH COINS MODAL */}
      {showCoinsModal && selectedUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-in fade-in">
          <div className="bg-[#0f1026] border border-amber-500/30 rounded-3xl p-8 max-w-md w-full shadow-2xl relative">
            <div className="flex items-center gap-3 text-amber-400 mb-4">
              <Coins size={28} />
              <h3 className="text-xl font-black text-white">Unban With Coins</h3>
            </div>

            <p className="text-sm text-slate-300 mb-6">
              If the banned user agreed to pay coins to reactivate their account, the specified coin amount will be deducted directly from their wallet.
            </p>

            <div className="p-4 rounded-xl bg-white/[0.03] border border-white/5 mb-6 space-y-2 text-xs">
              <div className="flex justify-between">
                <span className="text-slate-400">User:</span>
                <span className="font-bold text-white">{selectedUser.first_name} ({selectedUser.xoxo_id})</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">Current Wallet Balance:</span>
                <span className="font-mono font-bold text-amber-400">
                  {Number(selectedUser.coins_balance || 0).toLocaleString()} 🪙
                </span>
              </div>
            </div>

            <form onSubmit={handleUnbanWithCoins} className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-2">
                  Unban Coin Fee
                </label>
                <input
                  type="number"
                  min="1000"
                  step="500"
                  required
                  value={unbanFee}
                  onChange={(e) => setUnbanFee(Number(e.target.value))}
                  className="w-full bg-[#030712] border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none focus:border-amber-500 transition-colors font-mono font-bold"
                />
              </div>

              <div className="flex items-center gap-3 pt-4 border-t border-white/10">
                <button
                  type="button"
                  onClick={() => setShowCoinsModal(false)}
                  className="flex-1 py-3 rounded-xl bg-white/5 hover:bg-white/10 text-slate-300 text-sm font-semibold transition-colors cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={actionLoading || Number(selectedUser.coins_balance || 0) < unbanFee}
                  className="flex-1 py-3 rounded-xl bg-amber-600 hover:bg-amber-500 disabled:opacity-50 text-white text-sm font-bold transition-colors cursor-pointer"
                >
                  {actionLoading ? 'Deducting...' : `Pay ${unbanFee} & Unban`}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManagerUsers;
